import type { CreateMfaToken, TokenScope } from '@dynamic-labs/sdk-api-core';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';

import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { NoPasskeyCredentialsFoundError } from '../../../errors/NoPasskeyCredentialsFoundError';
import { NoWebAuthNSupportError } from '../../../errors/NoWebAuthNSupportError';
import { getPasskeyAuthenticationOptions } from '../../auth/passkeys/getPasskeyAuthenticationOptions';
import { serverAuthenticatePasskey } from '../../auth/passkeys/serverAuthenticatePasskey';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import { emitEvent } from '../../clientEvents';

type AuthenticatePasskeyMFAParams = {
  /** @deprecated Use `requestedScopes` instead. */
  createMfaToken?: CreateMfaToken;
  relatedOriginRpId?: string;
  requestedScopes?: TokenScope[];
};

/**
 * Authenticates using a passkey for multi-factor authentication.
 *
 * This function prompts the user to authenticate with their registered
 * passkey to complete an MFA challenge and obtain an MFA token.
 *
 * @param [params.createMfaToken] - @deprecated Use `requestedScopes` instead. Optional configuration for MFA token creation.
 * @param [params.requestedScopes] - Optional scopes to request an elevated access token instead of an MFA token.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the MFA authentication response.
 * @throws NoWebAuthNSupportError If WebAuthn is not supported by the browser.
 * @throws NoPasskeyCredentialsFoundError If no passkey credentials are found.
 */
export const authenticatePasskeyMFA = async (
  { createMfaToken, relatedOriginRpId, requestedScopes }: AuthenticatePasskeyMFAParams = {},
  client = getDefaultClient()
) => {
  const core = getCore(client);

  try {
    if (!core.passkey.isSupported()) {
      throw new NoWebAuthNSupportError();
    }

    const options = await getPasskeyAuthenticationOptions(
      { relatedOriginRpId },
      client
    );
    const allowCredentials = options.allowCredentials?.map((credential) => ({
      ...credential,
      type: 'public-key' as const,
    }));

    if (!allowCredentials?.length) {
      throw new NoPasskeyCredentialsFoundError();
    }

    // Ensure allowCredentials has the required type property
    const formattedOptions: PublicKeyCredentialRequestOptionsJSON = {
      ...options,
      allowCredentials,
    };

    const authentication = await core.passkey.authenticate({
      optionsJSON: formattedOptions,
    });

    const response = await serverAuthenticatePasskey(
      { authentication, createMfaToken, requestedScopes },
      client
    );

    updateAuthFromVerifyResponse({ response }, client);

    emitEvent(
      {
        args: { mfaToken: response.mfaToken },
        event: 'mfaCompletionSuccess',
      },
      client
    );

    return response;
  } catch (error) {
    emitEvent(
      {
        args: { error },
        event: 'mfaCompletionFailure',
      },
      client
    );
    throw error;
  }
};
